import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import type {
  PermissionsResponse,
  RoleType,
  PermissionLevel,
  RolePermission,
} from "@shared/permissions";
import {
  hasMinimumPermission,
  hasResourceAccess,
  isAdmin,
} from "@shared/permissions";
import { getQueryFn } from "@/lib/queryClient";
import {
  getFreshPermissionsCache,
  writePermissionsCache,
  type CachedPermissions,
} from "@/lib/permissionsCache";

const ERROR_REFETCH_INTERVAL_MS = 30_000;
const PERMISSIONS_URL = "/api/auth/permissions";

export function usePermissions() {
  const { token, isAuthenticated, user } = useAuth();
  const userId = user?.id ?? null;

  // The query key includes the current user id so react-query treats
  // different users as separate caches. Combined with an explicit
  // queryFn that pins the URL, this prevents an in-flight fetch for a
  // previously-signed-in user from resolving into the new user's cache
  // (which the write-effect below would otherwise persist under the
  // wrong user id). The default queryFn would build the URL from
  // queryKey.join("/"), so we provide our own.
  const query = useQuery<PermissionsResponse>({
    queryKey: [PERMISSIONS_URL, userId ? String(userId) : "anon"],
    queryFn: getQueryFn<PermissionsResponse>({
      on401: "throw",
      url: PERMISSIONS_URL,
    }),
    enabled: isAuthenticated && !!token && !!userId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | null)?.status;
      // Don't retry on auth-family failures — the shared layer has already
      // attempted a silent refresh and dispatched auth:expired on hard fail.
      if (status === 401 || status === 403) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    // While the live fetch is in error (upstream outage, network failure),
    // background-poll on a coarse cadence so the moment the upstream
    // recovers we promote fresh data and the gating component can drop the
    // "using cached data" banner. Each refetch also re-evaluates the cache
    // freshness window below, so cached permissions cannot outlive the
    // 15-minute TTL during a prolonged outage.
    refetchInterval: (q) => (q.state.error ? ERROR_REFETCH_INTERVAL_MS : false),
  });

  // On every successful fetch, persist a snapshot for the current user so
  // RequirePermission can serve cached roles during a brief upstream
  // outage. Keyed by the authenticated user id so a previous user's
  // permissions can never bleed into a different session. We additionally
  // verify the payload's own `user_id` matches the currently-authenticated
  // user before writing — defense-in-depth against a race where a stale
  // in-flight fetch (from a prior session in the same tab) lands after a
  // user switch.
  useEffect(() => {
    if (!query.data || !userId) return;
    if (String(query.data.user_id) !== String(userId)) return;
    writePermissionsCache(userId, query.data);
  }, [query.data, userId]);

  // When the live query is in a hard-error state, fall back to the cached
  // snapshot for THIS user (if it's still fresh). Recomputed on every
  // render — getFreshPermissionsCache enforces the 15-minute TTL on each
  // call, so a prolonged outage will stop serving cached roles within one
  // refetch cycle of the cache expiring instead of latching onto a stale
  // snapshot for the lifetime of the error state.
  const cachedFallback: CachedPermissions | null =
    query.isError && userId ? getFreshPermissionsCache(userId) : null;

  const effectiveData: PermissionsResponse | null =
    query.data ?? cachedFallback?.data ?? null;
  const isUsingCache = !query.data && !!cachedFallback;
  const roles = effectiveData?.roles ?? {};

  function hasRole(role: RoleType): boolean {
    return role in roles;
  }

  function hasPermission(role: RoleType, minLevel: PermissionLevel): boolean {
    const rolePermission = roles[role];
    if (!rolePermission) return false;
    return hasMinimumPermission(rolePermission.permission, minLevel);
  }

  function getPermissionLevel(role: RoleType): PermissionLevel | null {
    return roles[role]?.permission ?? null;
  }

  function getRoleData(role: RoleType): RolePermission["data"] | null {
    return roles[role]?.data ?? null;
  }

  function getAccessibleResources(
    role: RoleType,
    resourceType: "business" | "restaurant" | "profile"
  ): number[] {
    const rolePermission = roles[role];
    if (!rolePermission) return [];
    if (isAdmin(rolePermission.permission)) return [];
    if (!rolePermission.data) return [];

    switch (resourceType) {
      case "business": {
        if (rolePermission.data.business_ids)
          return rolePermission.data.business_ids;
        if (rolePermission.data.business_id != null)
          return [rolePermission.data.business_id];
        return [];
      }
      case "restaurant": {
        if (rolePermission.data.restaurant_ids)
          return rolePermission.data.restaurant_ids;
        if (rolePermission.data.restaurant_id != null)
          return [rolePermission.data.restaurant_id];
        return [];
      }
      case "profile": {
        if (rolePermission.data.profiles) return rolePermission.data.profiles;
        if (rolePermission.data.profile != null)
          return [rolePermission.data.profile];
        return [];
      }
      default:
        return [];
    }
  }

  function canAccessResource(
    role: RoleType,
    resourceType: "business" | "restaurant" | "profile",
    resourceId: number
  ): boolean {
    const rolePermission = roles[role];
    if (!rolePermission) return false;
    return hasResourceAccess(rolePermission, resourceType, resourceId);
  }

  function isPortalUser(): boolean {
    return Object.keys(roles).length > 0;
  }

  function isAdminUser(): boolean {
    return Object.values(roles).some((r) => isAdmin(r.permission));
  }

  function hasUnrestrictedAccess(role: RoleType): boolean {
    const rolePermission = roles[role];
    if (!rolePermission) return false;
    return isAdmin(rolePermission.permission);
  }

  return {
    permissions: effectiveData,
    roles,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,

    /**
     * True when the data being returned came from the local cache because
     * the live fetch failed (e.g. upstream 502). The gating component
     * renders a small banner explaining the degraded state.
     */
    isUsingCache,

    hasRole,
    hasPermission,
    getPermissionLevel,
    getRoleData,
    getAccessibleResources,
    canAccessResource,
    isPortalUser,
    isAdminUser,
    hasUnrestrictedAccess,
  };
}
